<template>
    <div>
        <v-alert color="info" text>
            {{ $t('permanent_pages_info') }}
        </v-alert>

        <v-btn color="primary" class="mb-4" @click="newCategory">
            {{ $t("Luo uusi osio") }}
        </v-btn>

        <v-simple-table v-if="categories.length > 0">
            <template v-slot:default>
                <thead>
                    <tr>
                        <th style="width: 50px;"></th>
                        <th>{{ $t('Osio') }}</th>
                        <th style="width: 100px;"></th>
                        <th style="width: 120px;">{{ $t('Tila') }}</th>
                    </tr>
                </thead>

                <draggable v-model="categories" tag="tbody" handle=".handle" ghost-class="ghost" @end="sortCategories">
                    <tr v-for="category in categories" :key="category.id"
                        style="border-bottom: thin solid rgba(94, 86, 105, 0.14)">
                        <td>
                            <v-icon class="handle" style="cursor: move;">
                                {{ icons.mdiCursorMove }}
                            </v-icon>
                        </td>
                        <td>
                            <a @click="editCategory(category)" style="cursor: pointer;">
                                {{ category.title }}
                            </a>
                        </td>
                        <td>
                            <v-chip v-if="category.is_default" color="primary" small>
                                {{ $t('Oletus') }}
                            </v-chip>
                        </td>
                        <td>
                            <v-icon v-if="category.is_active" size="24" color="success">
                                {{ icons.mdiCheckboxMarkedCircle }}
                            </v-icon>
                            <v-icon v-else size="24" color="error">
                                {{ icons.mdiCloseCircle }}
                            </v-icon>
                        </td>
                    </tr>
                </draggable>

            </template>
        </v-simple-table>

        <div v-else-if="!loading" class="text-center pa-4 text--secondary">
            {{ $t('Ei sivukategorioita') }}
        </div>

        <!-- Category Form Dialog -->
        <v-dialog v-model="formDialogVisible" max-width="600px" persistent>
            <v-card>
                <v-card-title>
                    <span class="text-h5">
                        {{ dialogMode === 'create' ? $t('Uusi sivukategoria') : $t('Muokkaa sivukategoriaa') }}
                    </span>
                </v-card-title>
                <v-card-text>
                    <div class="ma-5 text-center" v-if="isLoading">
                        <v-progress-circular class="mt-8" indeterminate :width="2"></v-progress-circular>
                    </div>
                    <v-form v-on:submit.prevent="saveCategory" ref="form" v-else>
                        <!-- Language Tabs -->
                        <v-card outlined elevation="0" class="mb-4">
                            <v-tabs v-model="tab" grow>
                                <v-tab v-for="lang in availableLanguages" :key="'langtab-' + lang.code">
                                    <img :src="lang.icon" style="max-height: 20px" />
                                    &nbsp;&nbsp;{{ lang.name }}
                                    &nbsp;
                                    <v-icon v-if="langOk(formData.translations, lang.code, ['title'])" color="success">
                                        {{ icons.mdiCheck }}
                                    </v-icon>
                                    <v-icon v-else color="error">
                                        {{ icons.mdiBlockHelper }}
                                    </v-icon>
                                </v-tab>
                            </v-tabs>
                            <v-tabs-items v-model="tab">
                                <v-tab-item v-for="lang in availableLanguages" :key="'langtabitem-' + lang.code">
                                    <div class="px-5 py-10">
                                        <v-text-field :id="'category-title-' + lang.code"
                                            v-model="formData.translations[lang.code].title" :label="$t('Osio')"
                                            outlined class="mb-4" hide-details="auto" />
                                    </div>
                                </v-tab-item>
                            </v-tabs-items>
                        </v-card>

                        <!-- Active Toggle -->
                        <v-switch v-if="!formData.is_default" v-model="formData.is_active"
                            :label="$t('Käytössä')"></v-switch>

                        <!-- Info about default category -->
                        <v-alert v-if="formData.is_default" type="info" dense text class="mt-4">
                            {{ $t('Tämä on oletuskategoria, jota ei voi poistaa.') }}
                        </v-alert>

                        <!-- Validation input -->
                        <v-input class="mb-3" hidden hide-details="auto" :rules="[validateLangs]"></v-input>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey darken-1" text @click="closeDialog">
                        {{ $t('Peruuta') }}
                    </v-btn>


                    <v-spacer></v-spacer>
                    <v-btn v-if="dialogMode === 'edit' && !formData.is_default" color="error"
                        @click="deleteCategory(selectedCategory)">
                        {{ $t('Poista') }}
                    </v-btn>
                    <v-btn color="primary" @click="saveCategory" :loading="saving">
                        {{ $t('Tallenna') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </div>
</template>

<script>
import { ref, computed, onMounted } from "@vue/composition-api";
import draggable from "vuedraggable";
import adminApi from "@/api/admin";
import { useTranslations } from "@/@core/libs/i18n";
import useAuth from "@/useAuth";
import usePageCategories from "@/@core/utils/usePageCategories";
import { mdiPencil, mdiDelete, mdiPlus, mdiCheck, mdiBlockHelper, mdiCheckboxMarkedCircle, mdiCloseCircle, mdiCursorMove } from "@mdi/js";
import { getValidTranslations, langOk } from '@/@core/utils/translation_helpers';
import { i18n } from '@/plugins/i18n';

export default {
    name: "PageCategories",
    components: {
        draggable
    },
    setup() {
        const { t } = useTranslations();
        const { tenant } = useAuth();
        const { refreshPageCategories } = usePageCategories();

        // State
        const loading = ref(false);
        const saving = ref(false);
        const isLoading = ref(false);
        const tab = ref(null);
        const form = ref(null);
        const categories = ref([]);
        const selectedCategory = ref(null);
        const formDialogVisible = ref(false);
        const dialogMode = ref('create');
        const isDeleting = ref(false);

        // Form data with translations structure
        const getEmptyFormData = () => {
            const translations = {};
            (tenant.value?.languages || []).forEach(lang => {
                translations[lang.code] = { title: '' };
            });
            return {
                id: null,
                translations,
                is_active: true,
                is_default: false
            };
        };

        const formData = ref(getEmptyFormData());

        // Get available languages from tenant
        const availableLanguages = computed(() => {
            return tenant.value?.languages || [];
        });

        // Determine primary language (first language or fallback)
        const primaryLanguage = computed(() => {
            return availableLanguages.value.length > 0 ? availableLanguages.value[0].code : 'fi';
        });

        // Table headers
        const headers = ref([
            { text: t('Nimi'), value: 'title', sortable: false },
            { text: '', value: 'is_default', sortable: false, width: '100px' },
            { text: t('Tila'), value: 'is_active', sortable: false, width: '120px' },
        ]);

        // Methods
        const fetchCategories = async () => {
            loading.value = true;
            try {
                const response = await adminApi.getPageCategories();
                if (response.ok) {
                    categories.value = response.data.results || response.data;
                } else {
                    console.error("Error fetching categories:", response.error);
                }
            } catch (error) {
                console.error("Error fetching categories:", error);
            } finally {
                loading.value = false;
            }
        };


        const getPageCategory = async (id) => {
            isLoading.value = true;
            try {
                const response = await adminApi.getPageCategory(id);
                if (response.ok) {
                    const category = response.data;

                    // Populate form data with fetched data
                    const translations = {};
                    availableLanguages.value.forEach(lang => {
                        if (category.translations && category.translations[lang.code]) {
                            translations[lang.code] = {
                                title: category.translations[lang.code].title || ''
                            };
                        } else {
                            translations[lang.code] = { title: '' };
                        }
                    });

                    formData.value = {
                        id: category.id,
                        translations,
                        is_active: category.is_active,
                        is_default: category.is_default
                    };
                } else {
                    alert(response.error || t('Kategorian hakeminen epäonnistui'));
                }
            } catch (error) {
                console.error("Error fetching category:", error);
                alert(t('Kategorian hakeminen epäonnistui'));
            } finally {
                isLoading.value = false;
            }
        };

        const newCategory = () => {
            formData.value = getEmptyFormData();
            selectedCategory.value = null;
            dialogMode.value = 'create';
            formDialogVisible.value = true;
        };

        const editCategory = (category) => {
            selectedCategory.value = category;
            dialogMode.value = 'edit';
            formDialogVisible.value = true;

            // Fetch fresh data from API
            getPageCategory(category.id);
        };

        const closeDialog = () => {
            formDialogVisible.value = false;
            selectedCategory.value = null;
            formData.value = getEmptyFormData();
        };

        const validateLangs = () => {
            let valid = true;
            tenant.value.languages.forEach((lang) => {
                if (!langOk(formData.value.translations, lang.code, ["title"]))
                    valid = false;
            });

            if (valid) {
                return true;
            } else {
                return i18n.t("Otsikko on pakollinen kaikilla kielillä");
            }
        };

        const saveCategory = async () => {
            // Validate using form
            const valid = form.value.validate();
            if (!valid) {
                return;
            }

            saving.value = true;
            try {
                const payload = {
                    translations: getValidTranslations(formData.value.translations, ['title']),
                    is_active: formData.value.is_active
                };

                if (formData.value.id) {
                    payload.id = formData.value.id;
                }

                const response = await adminApi.savePageCategory(payload);
                if (response.ok) {
                    closeDialog();
                    await fetchCategories();
                    // Refresh the categories in the menu
                    refreshPageCategories();
                } else {
                    console.error("Error saving category:", response.error);
                    alert(response.error || t('Sivukategorian tallentaminen epäonnistui'));
                }
            } catch (error) {
                console.error("Error saving category:", error);
                alert(t('Sivukategorian tallentaminen epäonnistui'));
            } finally {
                saving.value = false;
            }
        };



        const deleteCategory = async (categoryToDelete) => {
            if (!categoryToDelete) return;

            if (confirm("Haluatko varmasti poistaa osion?")) {
                isDeleting.value = true;
                try {
                    const response = await adminApi.deletePageCategory(categoryToDelete.id);
                    if (response.ok) {
                        formDialogVisible.value = false;
                        formData.value = getEmptyFormData();
                        await fetchCategories();
                        // Refresh the categories in the menu
                        refreshPageCategories();
                    } else {
                        console.error("Error deleting category:", response.error);
                        alert(response.error || t('Osion poistaminen epäonnistui'));
                    }
                } catch (error) {
                    console.error("Error deleting category:", error);
                    alert(t('Osion poistaminen epäonnistui'));
                } finally {
                    isDeleting.value = false;
                }
            }
        };

        const sortCategories = async () => {
            loading.value = true;
            try {
                const order = categories.value.map((c) => c.id);
                await adminApi.sortPageCategories(order);
                await fetchCategories();
                // Refresh the categories in the menu
                refreshPageCategories();
            } catch (error) {
                console.error("Error sorting categories:", error);
            } finally {
                loading.value = false;
            }
        };

        // Lifecycle
        onMounted(() => {
            fetchCategories();
        });

        return {
            loading,
            saving,
            isLoading,
            tab,
            form,
            categories,
            selectedCategory,
            formDialogVisible,
            dialogMode,
            isDeleting,
            formData,
            availableLanguages,
            primaryLanguage,
            headers,
            tenant,
            fetchCategories,
            newCategory,
            editCategory,
            closeDialog,
            validateLangs,
            saveCategory,
            deleteCategory,
            sortCategories,
            langOk,
            icons: {
                mdiPencil,
                mdiDelete,
                mdiPlus,
                mdiCheck,
                mdiBlockHelper,
                mdiCheckboxMarkedCircle,
                mdiCloseCircle,
                mdiCursorMove
            }
        };
    }
};
</script>

<style scoped>
.v-simple-table {
    width: 100%;
}

.ghost {
    opacity: 0.5;
    background: #f0f0f0;
}

.handle {
    cursor: move;
}
</style>
